import { ButtonFlatMedium, TitleMediumEmphasized, TitleSmall } from '@/design-system';
import { Box, Drawer } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import colors from '@/theme/colors';
import { Divider } from '../Divider';
import { Grade } from '@/pages/AccountInformation/AccountInformation.types';

const ITEM_HEIGHT = 40;

interface GradePickerDrawerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (val?: string) => void;
  initialValue?: string;
  grades: Grade[];
  groupName: string;
}

const GRADE_PICKER_DIALOG_TITLE_ID = 'grade-picker-dialog-title';

export const GradePickerDrawer: React.FC<GradePickerDrawerProps> = ({
  open,
  onClose,
  onSelect,
  initialValue,
  grades,
  groupName,
}) => {
  const [selected, setSelected] = useState(initialValue);

  const listRef = useRef<HTMLDivElement>(null);
  const isSnapping = useRef(false);

  const scrollToIndex = (index: number) => {
    if (listRef.current) {
      isSnapping.current = true;
      listRef.current.scrollTo({
        top: index * ITEM_HEIGHT,
        behavior: 'smooth',
      });
    }
  };

  const handleItemClick = (index: number) => {
    scrollToIndex(index);
    setSelected(grades[index].id);
  };

  const handleDone = () => {
    onSelect(selected);
    onClose();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      SlideProps={{
        onEntered: () => {
          if (listRef.current && initialValue !== undefined) {
            const index = grades.findIndex(g => g.id === initialValue);
            listRef.current.scrollTo({
              top: index * ITEM_HEIGHT,
              behavior: 'auto',
            });
            setSelected(initialValue);
          }
        },
      }}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': GRADE_PICKER_DIALOG_TITLE_ID,
      }}
      sx={{
        zIndex: 1500,
      }}
    >
      <h1 id={GRADE_PICKER_DIALOG_TITLE_ID} className="visually-hidden">
        Grade Picker
      </h1>
      <Box sx={{ background: '#efefef' }}>
        <Box display="flex" justifyContent="flex-end">
          <ButtonFlatMedium data-testid="clearAllButton" onClick={handleDone} fontColor={colors.shadesJungle800}>
            Done
          </ButtonFlatMedium>
        </Box>
        <Divider />
        <Box
          sx={{
            position: 'relative',
            height: ITEM_HEIGHT * 5,
            overflowY: 'scroll',
            scrollSnapType: 'y mandatory',
            px: 5,
            py: `${ITEM_HEIGHT * 2}px`,
            padding: '0px',
          }}
          ref={listRef}
        >
          <Box
            component="fieldset"
            position="relative"
            zIndex={2}
            mb="24px"
            mt="16px"
            sx={{ border: 'none', margin: 0, padding: 0 }}
          >
            <Box
              component="legend"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0, 0, 0, 0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              Select Grade
            </Box>
            {grades.map(({ id: gradeId, description }, i) => {
              const isSelected = gradeId === selected;
              const GradeLabel = isSelected ? TitleMediumEmphasized : TitleSmall;
              return (
                <Box
                  key={gradeId}
                  component="label"
                  sx={{
                    height: ITEM_HEIGHT,
                    scrollSnapAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isSelected ? colors.silver : '#efefef',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    mx: isSelected ? '8px' : '0px',
                    position: 'relative',
                    '&:focus-within': {
                      outline: `2px solid ${colors.focusOutlineColor}`,
                      outlineOffset: '-2px',
                    },
                  }}
                >
                  <input
                    type="radio"
                    name={groupName}
                    value={gradeId}
                    checked={isSelected}
                    onChange={() => handleItemClick(i)}
                    onClick={() => handleItemClick(i)}
                    style={{
                      position: 'absolute',
                      width: '1px',
                      height: '1px',
                      padding: 0,
                      margin: '-1px',
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      border: 0,
                    }}
                  />
                  <GradeLabel color={isSelected ? colors.prussian : colors.charcoal}>
                    {description}
                  </GradeLabel>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};
